<script lang="ts">
	/**
	 * FurnitureOverlay — absolutely-positioned Svelte layer that renders placed
	 * furniture items and their interactive handles (move, resize, rotate, delete)
	 * directly over the presence grid.
	 *
	 * All positions are stored as fractions (0..1) in the store and converted to
	 * pixel values using the current gridSize on every render, so items remain
	 * correctly placed when the canvas is resized.
	 */
	import { onDestroy } from 'svelte';
	import {
		placedFurnitures,
		selectedFurnitureId,
		furnituresVisible,
		selectFurniture,
		updatePlacedFurniture,
		deletePlacedFurniture,
	} from 'src/stores/features/radar/furniture';
	import type { PlacedFurniture } from '../../Types';

	export let gridSize: number;
	export let interactive: boolean = true;

	let overlayEl: HTMLDivElement;

	// ── Interaction state ─────────────────────────────────────────────────────

	type DragState = {
		kind: 'move';
		id: string;
		ox: number; // cursor offset from item left
		oy: number; // cursor offset from item top
		pointerId: number;
	};

	type ResizeState = {
		kind: 'resize';
		id: string;
		corner: 'nw' | 'ne' | 'sw' | 'se';
		startBox: { x: number; y: number; w: number; h: number };
		startRotation: number;
		// Fixed grid point (opposite corner) for rotation-aware resize
		startFixedGrid: { x: number; y: number } | null;
		pointerId: number;
	};

	type RotateState = {
		kind: 'rotate';
		id: string;
		startAngle: number;
		startRotation: number;
		startClientX: number;
		startClientY: number;
		didDrag: boolean;
		pointerId: number;
	};

	let activeState: DragState | ResizeState | RotateState | null = null;

	// ── Geometry helpers ──────────────────────────────────────────────────────

	/** Transform item-local coords (origin = item top-left) to grid pixel coords. */
	function localToGrid(
		itemX: number,
		itemY: number,
		itemW: number,
		itemH: number,
		rotation: number,
		lx: number,
		ly: number,
	): { x: number; y: number } {
		const rad = (rotation * Math.PI) / 180;
		const c = Math.cos(rad);
		const s = Math.sin(rad);
		const cx = itemX + itemW / 2;
		const cy = itemY + itemH / 2;
		const dx = lx - itemW / 2;
		const dy = ly - itemH / 2;
		return { x: cx + dx * c - dy * s, y: cy + dx * s + dy * c };
	}

	const SNAP_ANGLES = [0, 90, 180, 270];

	function snapToNearest90(rot: number): number {
		const r = ((rot % 360) + 360) % 360;
		return SNAP_ANGLES.reduce((a, b) => (Math.abs(a - r) <= Math.abs(b - r) ? a : b));
	}

	function snapToNext90(rot: number): number {
		const nearest = snapToNearest90(rot);
		const idx = (SNAP_ANGLES.indexOf(nearest) + 1) % 4;
		return SNAP_ANGLES[idx];
	}

	/** True if the controls row overflows below the canvas. */
	function shouldShowAbove(item: PlacedFurniture): boolean {
		const CONTROLS_H = 40;
		return (item.yFrac + item.hFrac) * gridSize + CONTROLS_H > gridSize;
	}

	// ── Interaction handlers ──────────────────────────────────────────────────

	function capturePointer(e: PointerEvent) {
		if (overlayEl) overlayEl.setPointerCapture(e.pointerId);
	}

	function handleItemPointerDown(e: PointerEvent, item: PlacedFurniture) {
		if (!interactive) return;
		e.stopPropagation();
		selectFurniture(item.id);

		// Only start drag on left button without a handle target
		if (e.button !== 0) return;
		const target = e.target as HTMLElement;
		if (target.closest('.furn-handle, .furn-rotate, .furn-delete')) return;

		// Compute cursor offset relative to the item's top-left IN grid coordinates.
		const rect = overlayEl?.getBoundingClientRect();
		const gx = rect ? e.clientX - rect.left : 0;
		const gy = rect ? e.clientY - rect.top : 0;
		const px = item.xFrac * gridSize;
		const py = item.yFrac * gridSize;
		activeState = {
			kind: 'move',
			id: item.id,
			ox: gx - px, // cursor offset from item left in grid-px
			oy: gy - py, // cursor offset from item top in grid-px
			pointerId: e.pointerId,
		};
		capturePointer(e);
	}

	function handleResizePointerDown(
		e: PointerEvent,
		item: PlacedFurniture,
		corner: 'nw' | 'ne' | 'sw' | 'se',
	) {
		if (!interactive || e.button !== 0) return;
		e.stopPropagation();

		const px = item.xFrac * gridSize;
		const py = item.yFrac * gridSize;
		const pw = item.wFrac * gridSize;
		const ph = item.hFrac * gridSize;
		const rot = item.rotation || 0;

		// Fixed corner (local) = the corner diagonally opposite to the drag handle
		const fixedLocal: [number, number] =
			corner === 'se'
				? [0, 0]
				: corner === 'sw'
					? [pw, 0]
					: corner === 'ne'
						? [0, ph]
						: /* nw */ [pw, ph];

		const startFixedGrid =
			rot !== 0 ? localToGrid(px, py, pw, ph, rot, fixedLocal[0], fixedLocal[1]) : null;

		activeState = {
			kind: 'resize',
			id: item.id,
			corner,
			startBox: { x: px, y: py, w: pw, h: ph },
			startRotation: rot,
			startFixedGrid,
			pointerId: e.pointerId,
		};
		capturePointer(e);
	}

	function handleRotatePointerDown(e: PointerEvent, item: PlacedFurniture) {
		if (!interactive || e.button !== 0) return;
		e.stopPropagation();

		const px = item.xFrac * gridSize;
		const py = item.yFrac * gridSize;
		const pw = item.wFrac * gridSize;
		const ph = item.hFrac * gridSize;
		const cx = px + pw / 2;
		const cy = py + ph / 2;

		const rect = overlayEl?.getBoundingClientRect();
		const gx = rect ? e.clientX - rect.left : e.clientX;
		const gy = rect ? e.clientY - rect.top : e.clientY;

		activeState = {
			kind: 'rotate',
			id: item.id,
			startAngle: Math.atan2(gy - cy, gx - cx),
			startRotation: item.rotation || 0,
			startClientX: e.clientX,
			startClientY: e.clientY,
			didDrag: false,
			pointerId: e.pointerId,
		};
		capturePointer(e);
	}

	// Overlay-level pointermove/up (captures any pointer event on the overlay after
	// setPointerCapture, regardless of where the pointer actually is)
	function onOverlayPointerMove(e: PointerEvent) {
		if (!activeState || activeState.pointerId !== e.pointerId) return;

		const overlayEl = e.currentTarget as HTMLElement;
		const rect = overlayEl.getBoundingClientRect();
		const gx = e.clientX - rect.left;
		const gy = e.clientY - rect.top;

		if (activeState.kind === 'move') {
			doMove(activeState, gx, gy);
		} else if (activeState.kind === 'resize') {
			doResize(activeState, gx, gy);
		} else if (activeState.kind === 'rotate') {
			doRotate(activeState, gx, gy, e);
		}
	}

	function onOverlayPointerUp(e: PointerEvent) {
		if (!activeState || activeState.pointerId !== e.pointerId) return;

		if (activeState.kind === 'rotate' && !activeState.didDrag) {
			// Short click on rotate handle → snap to next 90°
			const item = $placedFurnitures.find((f) => f.id === activeState!.id);
			if (item) updatePlacedFurniture({ ...item, rotation: snapToNext90(item.rotation) });
		}

		activeState = null;
	}

	// ── Move ─────────────────────────────────────────────────────────────────

	function doMove(state: DragState, gx: number, gy: number) {
		const item = $placedFurnitures.find((f) => f.id === state.id);
		if (!item) return;

		const pw = item.wFrac * gridSize;
		const ph = item.hFrac * gridSize;
		// state.ox = cursor offset from item left at pointer-down, so new item left = gx - ox
		const rawX = gx - state.ox;
		const rawY = gy - state.oy;
		const clampedX = Math.max(0, Math.min(gridSize - pw, rawX));
		const clampedY = Math.max(0, Math.min(gridSize - ph, rawY));

		updatePlacedFurniture({
			...item,
			xFrac: clampedX / gridSize,
			yFrac: clampedY / gridSize,
		});
	}

	// ── Resize ────────────────────────────────────────────────────────────────

	function doResize(state: ResizeState, gx: number, gy: number) {
		const item = $placedFurnitures.find((f) => f.id === state.id);
		if (!item) return;

		const { corner, startBox, startRotation, startFixedGrid } = state;
		const MIN = 16; // px minimum size
		let x = startBox.x;
		let y = startBox.y;
		let w = startBox.w;
		let h = startBox.h;

		if (startFixedGrid !== null && startRotation !== 0) {
			// Rotation-aware resize (same algorithm as the reference)
			const rad = (startRotation * Math.PI) / 180;
			const c = Math.cos(rad);
			const s = Math.sin(rad);
			const dx = (gx - startFixedGrid.x) * c + (gy - startFixedGrid.y) * s;
			const dy = -(gx - startFixedGrid.x) * s + (gy - startFixedGrid.y) * c;

			if (corner === 'se') {
				w = Math.max(MIN, dx);
				h = Math.max(MIN, dy);
			} else if (corner === 'sw') {
				w = Math.max(MIN, -dx);
				h = Math.max(MIN, dy);
			} else if (corner === 'ne') {
				w = Math.max(MIN, dx);
				h = Math.max(MIN, -dy);
			} else {
				w = Math.max(MIN, -dx);
				h = Math.max(MIN, -dy);
			}

			// Preserve aspect ratio (Shift bypasses; we always lock here for simplicity)
			const R = startBox.w / startBox.h;
			if (Number.isFinite(R) && R > 0) {
				if (Math.abs(w - startBox.w) >= Math.abs(h - startBox.h)) h = w / R;
				else w = h * R;
				w = Math.max(MIN, w);
				h = Math.max(MIN, h);
			}

			// Recompute position so the fixed corner stays put
			const fixLx = corner === 'se' ? 0 : corner === 'sw' ? w : corner === 'ne' ? 0 : w;
			const fixLy = corner === 'se' ? 0 : corner === 'sw' ? 0 : corner === 'ne' ? h : h;
			const cOffX = w / 2 - fixLx;
			const cOffY = h / 2 - fixLy;
			const newCx = startFixedGrid.x + cOffX * c - cOffY * s;
			const newCy = startFixedGrid.y + cOffX * s + cOffY * c;
			x = newCx - w / 2;
			y = newCy - h / 2;
		} else {
			// Axis-aligned resize (no rotation)
			if (corner === 'se') {
				w = Math.max(MIN, gx - startBox.x);
				h = Math.max(MIN, gy - startBox.y);
			} else if (corner === 'sw') {
				w = Math.max(MIN, startBox.x + startBox.w - gx);
				x = startBox.x + startBox.w - w;
				h = Math.max(MIN, gy - startBox.y);
			} else if (corner === 'ne') {
				w = Math.max(MIN, gx - startBox.x);
				h = Math.max(MIN, startBox.y + startBox.h - gy);
				y = startBox.y + startBox.h - h;
			} else {
				w = Math.max(MIN, startBox.x + startBox.w - gx);
				x = startBox.x + startBox.w - w;
				h = Math.max(MIN, startBox.y + startBox.h - gy);
				y = startBox.y + startBox.h - h;
			}

			// Preserve aspect ratio
			const R = startBox.w / startBox.h;
			if (Number.isFinite(R) && R > 0) {
				if (Math.abs(w - startBox.w) >= Math.abs(h - startBox.h)) {
					h = w / R;
					if (corner === 'ne' || corner === 'nw') y = startBox.y + startBox.h - h;
				} else {
					w = h * R;
					if (corner === 'sw' || corner === 'nw') x = startBox.x + startBox.w - w;
				}
				w = Math.max(MIN, w);
				h = Math.max(MIN, h);
			}

			x = Math.max(0, Math.min(gridSize - w, x));
			y = Math.max(0, Math.min(gridSize - h, y));
		}

		// Clamp rotated items too
		x = Math.max(0, Math.min(gridSize - w, x));
		y = Math.max(0, Math.min(gridSize - h, y));

		updatePlacedFurniture({
			...item,
			xFrac: x / gridSize,
			yFrac: y / gridSize,
			wFrac: w / gridSize,
			hFrac: h / gridSize,
		});
	}

	// ── Rotate ────────────────────────────────────────────────────────────────

	function doRotate(state: RotateState, gx: number, gy: number, e: PointerEvent) {
		const dist = Math.hypot(e.clientX - state.startClientX, e.clientY - state.startClientY);
		if (dist > 4) state.didDrag = true;
		if (!state.didDrag) return;

		const item = $placedFurnitures.find((f) => f.id === state.id);
		if (!item) return;

		const px = item.xFrac * gridSize;
		const py = item.yFrac * gridSize;
		const pw = item.wFrac * gridSize;
		const ph = item.hFrac * gridSize;
		const cx = px + pw / 2;
		const cy = py + ph / 2;

		const angle = Math.atan2(gy - cy, gx - cx);
		let delta = (angle - state.startAngle) * (180 / Math.PI);
		let rot = state.startRotation + delta;
		rot = ((rot % 360) + 360) % 360;
		if (rot > 180) rot -= 360;

		updatePlacedFurniture({ ...item, rotation: rot });
	}

	function handleDeleteClick(e: MouseEvent, id: string) {
		e.stopPropagation();
		deletePlacedFurniture(id);
	}

	onDestroy(() => {
		activeState = null;
	});
</script>

<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
	class="furniture-overlay"
	bind:this={overlayEl}
	on:pointermove={onOverlayPointerMove}
	on:pointerup={onOverlayPointerUp}
	on:pointercancel={onOverlayPointerUp}
>
	{#if $furnituresVisible && gridSize > 0}
		{#each $placedFurnitures as item (item.id)}
			{@const px = item.xFrac * gridSize}
			{@const py = item.yFrac * gridSize}
			{@const pw = item.wFrac * gridSize}
			{@const ph = item.hFrac * gridSize}
			{@const sel = $selectedFurnitureId === item.id}
			{@const above = shouldShowAbove(item)}

			<!-- svelte-ignore a11y-no-static-element-interactions -->
			<div
				class="furn-item"
				class:selected={sel}
				style="left:{px}px; top:{py}px; width:{pw}px; height:{ph}px; transform:rotate({item.rotation}deg)"
				on:pointerdown={(e) => handleItemPointerDown(e, item)}
			>
				<img
					src="./images/furnitures/{item.src}"
					alt={item.name}
					draggable="false"
					style="width:100%; height:100%; object-fit:contain; pointer-events:none;"
				/>

				{#if sel && interactive}
					<!-- 4 corner resize handles -->
					<span
						class="furn-handle nw"
						aria-label="Resize"
						on:pointerdown={(e) => handleResizePointerDown(e, item, 'nw')}
					></span>
					<span
						class="furn-handle ne"
						aria-label="Resize"
						on:pointerdown={(e) => handleResizePointerDown(e, item, 'ne')}
					></span>
					<span
						class="furn-handle sw"
						aria-label="Resize"
						on:pointerdown={(e) => handleResizePointerDown(e, item, 'sw')}
					></span>
					<span
						class="furn-handle se"
						aria-label="Resize"
						on:pointerdown={(e) => handleResizePointerDown(e, item, 'se')}
					></span>

					<!-- Controls row (rotate + delete) -->
					<div class="furn-controls" class:above>
						<span
							class="furn-rotate"
							aria-label="Rotate (drag for free rotation, click for 90°)"
							title="Drag to rotate freely · Click for 90°"
							on:pointerdown={(e) => handleRotatePointerDown(e, item)}
						>
							<i class="fas fa-rotate-right" aria-hidden="true"></i>
						</span>
						<button
							class="furn-delete"
							aria-label="Delete furniture"
							title="Delete"
							on:click={(e) => handleDeleteClick(e, item.id)}
						>
							<i class="fas fa-trash" aria-hidden="true"></i>
						</button>
					</div>
				{/if}
			</div>
		{/each}
	{/if}
</div>

<style>
	.furniture-overlay {
		position: absolute;
		inset: 0;
		pointer-events: none;
		z-index: 10;
		overflow: hidden;
		border-radius: 10px;
	}

	/* ── Furniture item ──────────────────────────────────────────────────────── */
	.furn-item {
		position: absolute;
		pointer-events: auto;
		cursor: grab;
		transform-origin: center center;
		user-select: none;
		border-radius: 3px;
		transition: outline 0.08s;
	}

	.furn-item:active {
		cursor: grabbing;
	}

	.furn-item.selected {
		outline: 2px solid #2491ff;
		outline-offset: 2px;
	}

	/* ── Resize handles (corners) ────────────────────────────────────────────── */
	.furn-handle {
		position: absolute;
		width: 12px;
		height: 12px;
		background: #fff;
		border: 2px solid #2491ff;
		border-radius: 3px;
		pointer-events: auto;
		z-index: 2;
		box-sizing: border-box;
	}

	.furn-handle.nw {
		top: -6px;
		left: -6px;
		cursor: nw-resize;
	}
	.furn-handle.ne {
		top: -6px;
		right: -6px;
		cursor: ne-resize;
	}
	.furn-handle.sw {
		bottom: -6px;
		left: -6px;
		cursor: sw-resize;
	}
	.furn-handle.se {
		bottom: -6px;
		right: -6px;
		cursor: se-resize;
	}

	/* ── Controls row (rotate + delete) ─────────────────────────────────────── */
	.furn-controls {
		position: absolute;
		left: 50%;
		transform: translateX(-50%);
		top: calc(100% + 8px); /* default: below the item */
		display: flex;
		gap: 4px;
		pointer-events: auto;
		z-index: 3;
		background: rgba(20, 21, 24, 0.88);
		border: 1px solid rgba(255, 255, 255, 0.12);
		border-radius: 999px;
		padding: 4px 6px;
		backdrop-filter: blur(6px);
		white-space: nowrap;
	}

	.furn-controls.above {
		top: auto;
		bottom: calc(100% + 8px); /* above the item */
	}

	.furn-rotate,
	.furn-delete {
		width: 28px;
		height: 28px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 13px;
		cursor: pointer;
		color: #eaeaea;
		border: none;
		background: transparent;
		transition:
			background 0.12s,
			color 0.12s;
		touch-action: none;
	}

	.furn-rotate:hover {
		background: rgba(255, 255, 255, 0.12);
		color: #fff;
	}

	.furn-delete:hover {
		background: rgba(220, 53, 69, 0.2);
		color: #dc3545;
	}
</style>
